import InfiniteScroll from 'react-infinite-scroll-component';
import { Disclosure } from '@headlessui/react';
import isEmpty from 'lodash/isEmpty';
// import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import { useCallback, useContext, useEffect, useId, useState } from 'react';
// import ArchiveSwitch from '@/modules/common/components/ArchiveSwitch.tsx';
import ReportItem from '@/modules/common/components/items/ReportItem.tsx';
import CreateReportModal from '@/modules/common/components/modals/CreateReportModal.tsx';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { useReportsListQuery } from '@/modules/common/queries/reports.ts';
import ReportsFilters from '@/modules/admin-dashboard/components/OnDemandReportsFilters.tsx';
import { Button, EmptyListState, ModalProviderContext, PageLayout, SearchField, Spinner } from 'ui';
import { FiltersIcon } from 'ui/icons';
import { ReportFormat, ReportType } from 'compliance-api';

export interface Filters {
  archived?: boolean;
  filter_by_type?: ReportType;
  filter_by_format?: ReportFormat;
  dateRange?: [string, string];
}

interface OnDemandReportsProps {
  clientId?: number;
}

const OnDemandReports = ({ clientId }: OnDemandReportsProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const businessId = useGetCurrentBusinessId();
  const [filters, setFilters] = useState<Filters>({});
  const [searchValue, setSearchValue] = useState('');

  const containerId = useId();

  useEffect(() => {
    setFilters({});
    setSearchValue('');
  }, [businessId, clientId]);

  const { data, isFetching, fetchNextPage, hasNextPage } = useReportsListQuery(
    {
      page: 1,
      page_size: 18,
      ...pickBy(
        {
          ...(searchValue ? { filter_by_name: searchValue } : {}),
          filter_by_type: filters.filter_by_type,
          filter_by_format: filters.filter_by_format,
          filter_by_created_date_start: filters.dateRange?.[0],
          filter_by_created_date_end: filters.dateRange?.[1],
          filter_by_client_id: clientId,
        },
        Boolean,
      ),
    },
    false,
    { enableGeneratingPolling: true },
  );

  const isInitialLoading = !data && isFetching;

  const reports = (data?.pages || []).flatMap(({ data }) => data);

  const isSearchUsed = !isEmpty({ ...filters, ...(searchValue ? { search: searchValue } : {}) });

  const handleOpenCreateReportModal = useCallback(() => {
    openModal(CreateReportModal, { clientId });
  }, [openModal, clientId]);

  // const handleToggleArchiveFilter = useCallback((active: boolean) => {
  //   setFilters(filters => (active ? { ...filters, archived: true } : omit(filters, ['archived'])));
  // }, []);
  return (
    <Disclosure>
      <PageLayout title="On demand reports">
        <div className="h-full flex flex-col gap-4">
          <div className="flex flex-wrap-reverse w-full mt-5 md:mt-0 justify-end items-center gap-3">
            {/*<ArchiveSwitch active={!!filters.archived} onChange={handleToggleArchiveFilter} />*/}
            <div className="flex-1 min-w-64">
              <SearchField placeholder="Search" value={searchValue} onChange={setSearchValue} />
            </div>
            <div className="w-auto">
              <Disclosure.Button as="div">
                <Button icon={FiltersIcon} variant="secondary">
                  Filters
                </Button>
              </Disclosure.Button>
            </div>
            <div className="min-w-fit">
              <Button onClick={handleOpenCreateReportModal} variant="primary">
                Generate new report
              </Button>
            </div>
          </div>

          <ReportsFilters filters={filters} onFilterChange={setFilters} />

          {isInitialLoading && (
            <div className="w-full h-full flex items-center justify-center">
              <Spinner size="lg" />
            </div>
          )}
          <EmptyListState
            title="No reports created yet"
            description="Reports allow you to export task execution results, overdue statistics and compliance statuses"
            isLoading={isInitialLoading}
            isSearchUsed={isSearchUsed}
            itemsCount={reports.length}
            classes={{ container: '!h-auto flex-1' }}
          />
          {!isInitialLoading && !!reports.length && (
            <div id={containerId} className="overflow-y-auto">
              <InfiniteScroll
                next={fetchNextPage}
                dataLength={reports.length}
                hasMore={!!hasNextPage}
                className="w-full flex flex-col space-y-2"
                style={{ overflow: 'unset' }}
                loader={
                  <div className="flex justify-center items-center h-16 w-full">
                    <Spinner size="md" variant="primary" />
                  </div>
                }
                scrollableTarget={containerId}
              >
                {reports.map(report => (
                  <ReportItem key={report.id} report={report} />
                ))}
              </InfiniteScroll>
            </div>
          )}
        </div>
      </PageLayout>
    </Disclosure>
  );
};

export default OnDemandReports;
